import { storeToRefs } from 'pinia';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import useClientMatterStore from '@/services/useClientMatterStore';
export function useRateGroupLogic() {
  const {
    companySettings
  } = storeToRefs(useCompanyStore());
  const clientMatterStore = useClientMatterStore();
  const shouldFetchMatterDetails = invoiceState => {
    return companySettings.value.isRateGroupEnabled && (!invoiceState.matter?.rateGroup || invoiceState.matters && invoiceState.matters.length > 0 && !invoiceState.matters[0].rateGroup);
  };
  const updateSingleMatter = (invoiceState, matter) => {
    if (invoiceState.matter && matter) {
      invoiceState.matter = matter;
    }
  };
  const updateMattersArray = (invoiceState, matter, matterId) => {
    if (invoiceState.matters && invoiceState.matters.length > 0) {
      const matterIndex = invoiceState.matters.findIndex(m => m.id === matterId);
      if (matterIndex >= 0) {
        invoiceState.matters[matterIndex] = matter;
      }
    }
  };
  const fetchMatterDetailsIfNeeded = async (matterId, invoiceState, handleError) => {
    if (!shouldFetchMatterDetails(invoiceState)) {
      return;
    }
    try {
      const matter = await clientMatterStore.getMatterById(matterId, true);
      updateSingleMatter(invoiceState, matter);
      updateMattersArray(invoiceState, matter, matterId);
    } catch (error) {
      handleError('Error fetching matter details');
    }
  };
  const findMatterSpecificRate = (userId, rates, matterUsers) => {
    if (!userId || !matterUsers?.length) {
      return null;
    }
    const matterUser = matterUsers.find(user => user.id === userId);

    // Check if matter user has a valid rate with amount
    if (matterUser?.rate !== undefined && typeof matterUser.rate.amount === 'number') {
      // Use the label from the API if it exists and is not empty, otherwise use 'Custom'
      const label = matterUser.rate.label && matterUser.rate.label.trim() !== '' ? matterUser.rate.label : 'Custom';
      return {
        label,
        amount: matterUser.rate.amount
      };
    }
    return null;
  };
  const buildBasicRateList = rates => {
    let list = [];
    if (rates.standard !== undefined) {
      list.push({
        label: 'Standard',
        amount: rates.standard
      });
    }
    if (rates.other?.length) {
      const others = rates.other.map(rate => ({
        ...rate,
        value: rate.label
      }));
      list = [...list, ...others];
    }
    return list;
  };
  const addRateGroupToList = (rateList, matter, userId) => {
    if (!matter?.rateGroup || !companySettings.value.isRateGroupEnabled) {
      return null;
    }
    const rateGroupAccount = matter.rateGroup.rateGroupAccounts?.find(u => u.accountId === userId);
    if (!rateGroupAccount?.rate) {
      return null;
    }
    const label = matter.rateGroup.name;
    if (!rateList.find(item => item.label === label)) {
      const rateGroupRate = {
        label,
        amount: rateGroupAccount.rate,
        rateGroup: true
      };
      rateList.push(rateGroupRate);
      return rateGroupRate;
    }
    return rateList.find(r => r.label === label);
  };

  // Helper function to check if two rates are equivalent
  const ratesMatch = (rate1, rate2) => {
    if (!rate1 || !rate2) return false;
    return rate1.label === rate2.label && rate1.amount === rate2.amount;
  };
  const selectRateForNewEntry = ({
    rateGroupRate,
    matterSpecificRate,
    rateList,
    isExistingEntry,
    form,
    matter,
    isMatterChange = false,
    isUserChange = false,
    // New parameter to detect user/timekeeper changes
    isDuplicate = false,
    // New parameter to detect duplicates
    originalEntry = null,
    // Original entry to detect real matter changes
    matterChangedDuringSession = false // Flag to track if user has changed matters during editing session
  }) => {
    // For existing entries, detect real matter changes by comparing current matter with original entry's matter
    const originalMatterId = originalEntry?.matter?.id;
    const currentMatterId = matter?.id;
    const hasActualMatterChange = isExistingEntry && originalMatterId && currentMatterId && originalMatterId !== currentMatterId;

    // For existing entries, preserve the current rate UNLESS:
    // 1. User/timekeeper has changed (isUserChange=true)
    // 2. User has actively changed to a different matter (actual matter ID change)
    // 3. User has changed matters during this editing session (matterChangedDuringSession=true)
    if (isExistingEntry && form?.rate && !isUserChange) {
      // Check if this is a real matter change (different matter IDs) vs initial loading
      const isRealMatterChange = hasActualMatterChange && isMatterChange;

      // Don't preserve custom rates if user has changed matters during this session
      if (!isRealMatterChange && !matterChangedDuringSession) {
        const matchingRate = selectRateForExistingEntry(form, rateList);
        // If we found a matching rate in the list, use it (preserve existing rate)
        if (matchingRate) {
          return matchingRate;
        }
        // If no match found, fall through to new selection logic
      }
    }

    // SPECIAL CASE: For duplication and new entries - preserve original rate only when NOT changing matters
    // This allows rate updates when users actively change matters/clients during creation or duplication
    if (!isExistingEntry && form?.rate && form.rate.amount !== undefined && form.rate.amount !== null && typeof form.rate.label === 'string' && form.rate.label.trim() !== '' && !isMatterChange // Only preserve when not changing matters (works for both duplicates and new entries)
    ) {
      const originalRate = form.rate;

      // Try to find the exact rate in the rate list
      const exactMatch = rateList.find(r => ratesMatch(r, originalRate));
      if (exactMatch) {
        return exactMatch; // Return the rate from the list (maintains reference consistency)
      }

      // If not found in the list, return the original rate
      // The rate list building logic should have already added it to the dropdown
      return originalRate;
    }

    // Prioritize rate group rate over matter-specific rate since rate group rates have proper labels
    if (rateGroupRate) {
      return rateGroupRate;
    }

    // Only use matter-specific rate if no rate group rate exists and it's truly different
    if (matterSpecificRate && !rateGroupRate) {
      return matterSpecificRate;
    }
    const fallbackRate = rateList.length > 0 ? rateList[0] : null;
    return fallbackRate;
  };
  const selectRateForExistingEntry = (form, rateList) => {
    const {
      rate
    } = form;
    const {
      label: entryLabel,
      amount: entryAmount
    } = rate || {};
    const matchingRate = rateList.find(r => r.label === entryLabel && r.amount === entryAmount);
    return matchingRate || rate;
  };
  const getRateListWithSelection = (account, matter, isExistingEntry = false, existingForm = null, isMatterChange = false, isUserChange = false,
  // New parameter to detect user changes
  isDuplicate = false,
  // New parameter to detect duplicates
  originalEntry = null,
  // Original entry to detect real matter changes
  matterChangedDuringSession = false // Flag to track if user has changed matters during editing session
  ) => {
    const rates = account?.rates || [];
    const userId = account?.id;

    // Step 1: Find matter-specific rate
    const matterSpecificRate = findMatterSpecificRate(userId, rates, matter.users);

    // Step 2: Build basic rate list
    const rateList = buildBasicRateList(rates);

    // Step 3: Add rate group to list
    const rateGroupRate = addRateGroupToList(rateList, matter, userId);

    // Step 4: Add matter-specific rate to list if it exists and isn't already there
    if (matterSpecificRate && !rateList.find(r => r.label === matterSpecificRate.label && r.amount === matterSpecificRate.amount)) {
      rateList.push(matterSpecificRate);
    }

    // Step 5: For duplication cases, ensure the original rate appears in the dropdown
    // This handles cases where the duplicated entry has a custom rate not available in the new matter
    if (!isExistingEntry && existingForm?.rate && !isMatterChange && existingForm.rate.amount !== undefined && existingForm.rate.amount !== null && typeof existingForm.rate.label === 'string' && existingForm.rate.label.trim() !== '') {
      const originalRate = existingForm.rate;
      const exactMatch = rateList.find(r => ratesMatch(r, originalRate));
      if (!exactMatch) {
        // Add the original rate to the list so it appears in the dropdown
        // Insert it before the "Custom" placeholder to maintain logical order
        const customIndex = rateList.findIndex(r => r.label === 'Custom' && r.amount === 0);
        if (customIndex >= 0) {
          rateList.splice(customIndex, 0, {
            ...originalRate
          }); // Insert before Custom
        } else {
          rateList.push({
            ...originalRate
          }); // Add at end if no Custom found yet
        }
      }
    }

    // Step 5.5: For existing entries, ensure their custom rate is in the rate list
    // This handles cases where the existing entry has a custom rate not available in the matter's rate list
    if (isExistingEntry && existingForm?.rate && existingForm.rate.amount !== undefined && existingForm.rate.amount !== null && typeof existingForm.rate.label === 'string' && existingForm.rate.label.trim() !== '') {
      const originalRate = existingForm.rate;
      const exactMatch = rateList.find(r => ratesMatch(r, originalRate));
      if (!exactMatch) {
        // Add the original rate to the list so it appears in the dropdown
        // Insert it before the "Custom" placeholder to maintain logical order
        const customIndex = rateList.findIndex(r => r.label === 'Custom' && r.amount === 0);
        if (customIndex >= 0) {
          rateList.splice(customIndex, 0, {
            ...originalRate
          }); // Insert before Custom
        } else {
          rateList.push({
            ...originalRate
          }); // Add at end if no Custom found yet
        }
      }
    }

    // Step 6: Always add a "Custom" option for manual rate entry (if not already present)
    // Only add if matter-specific rate is not already labeled as "Custom"
    if (!rateList.find(r => r.label === 'Custom')) {
      rateList.push({
        label: 'Custom',
        amount: 0
      });
    }

    // Step 7: Select appropriate rate
    let selectedRate;
    // Use selectRateForNewEntry when:
    // - User changes (force new rate for new user)
    // - Matter changes (force new rate for new matter) 
    // - It's a new entry
    // Use selectRateForExistingEntry only for existing entries with no changes
    if (isExistingEntry && existingForm && !isUserChange && !isMatterChange) {
      selectedRate = selectRateForExistingEntry(existingForm, rateList);
    } else {
      selectedRate = selectRateForNewEntry({
        rateGroupRate,
        matterSpecificRate,
        rateList,
        isExistingEntry,
        form: existingForm,
        matter,
        isMatterChange,
        isUserChange,
        isDuplicate,
        originalEntry,
        matterChangedDuringSession
      });
    }
    return {
      rateList,
      selectedRate,
      rateGroupRate,
      matterSpecificRate
    };
  };
  return {
    shouldFetchMatterDetails,
    fetchMatterDetailsIfNeeded,
    getRateListWithSelection,
    findMatterSpecificRate,
    buildBasicRateList,
    addRateGroupToList,
    selectRateForNewEntry,
    selectRateForExistingEntry
  };
}